import { Injectable } from '@angular/core';
import { createClient, SupabaseClient, User, AuthResponse } from '@supabase/supabase-js';
import { BehaviorSubject, Observable } from 'rxjs';
import { environment } from '../../../environments/environment';

export interface ExamSession {
  id: string;
  user_id: string;
  exam_id: string;
  status: 'started' | 'in_progress' | 'completed' | 'paused';
  current_question: number;
  total_questions: number;
  start_time: string;
  last_activity: string;
  progress_percentage: number;
}

export interface ExamProgress {
  question_number: number;
  answer: string;
  time_spent: number;
  is_correct?: boolean;
}

// Version identifier to verify latest code is running
const SERVICE_VERSION = 'v3.1.0-monkey-patch-debug-fixed';

// Global error handler for NavigatorLockAcquireTimeoutError (keeping as backup)
function setupGlobalErrorHandler() {
  if (typeof window !== 'undefined') {
    window.addEventListener('error', (event) => {
      if (event.error && event.error.message && 
          event.error.message.includes('NavigatorLockAcquireTimeoutError')) {
        console.warn(`🔧 SupabaseService: [${SERVICE_VERSION}] Caught NavigatorLockAcquireTimeoutError, ignoring...`);
        console.warn(`🔍 [Debug] LockManager Error Details:`, {
          message: event.error.message,
          stack: event.error.stack,
          timestamp: new Date().toISOString()
        });
        event.preventDefault();
        return false;
      }
      return true;
    });

    window.addEventListener('unhandledrejection', (event) => {
      if (event.reason && event.reason.message && 
          event.reason.message.includes('NavigatorLockAcquireTimeoutError')) {
        console.warn(`🔧 SupabaseService: [${SERVICE_VERSION}] Caught unhandled NavigatorLockAcquireTimeoutError, ignoring...`);
        console.warn(`🔍 [Debug] LockManager Rejection Details:`, {
          message: event.reason.message,
          stack: event.reason.stack,
          timestamp: new Date().toISOString()
        });
        event.preventDefault();
        return false;
      }
      return true;
    });
  }
}

// Set up global error handler immediately
setupGlobalErrorHandler();

// 🔧 MONKEY-PATCH: Debug createClient calls
const originalCreateClient = createClient;
const createClientWithDebug = function (supabaseUrl: string, supabaseKey: string, options?: any) {
  console.warn('⚠️ [Debug] createClient() called from SupabaseService', {
    supabaseUrl: supabaseUrl?.substring(0, 20) + '...',
    supabaseKey: supabaseKey?.substring(0, 10) + '...',
    options: options ? 'provided' : 'undefined',
    stack: new Error().stack?.split('\n').slice(1, 6).join('\n'),
    timestamp: new Date().toISOString()
  });
  return originalCreateClient(supabaseUrl, supabaseKey, options);
};

// 🔧 ADDITIONAL DEBUG: Track all Supabase imports and usage
console.warn('🔍 [Debug] SupabaseService module loaded', {
  timestamp: new Date().toISOString(),
  createClient: typeof createClient,
  SupabaseClient: typeof SupabaseClient
});

// Singleton Supabase client to prevent multiple instances
let supabaseInstance: SupabaseClient | null = null;
let instanceCount = 0;
let initializationPromise: Promise<SupabaseClient> | null = null;

function createSupabaseClient(): Promise<SupabaseClient> {
  instanceCount++;
  console.log(`🔧 SupabaseService: [${SERVICE_VERSION}] Instance count: ${instanceCount}`);
  
  if (supabaseInstance) {
    console.log(`🔧 SupabaseService: [${SERVICE_VERSION}] Reusing existing Supabase client instance`);
    return Promise.resolve(supabaseInstance);
  }

  if (initializationPromise) {
    console.log(`🔧 SupabaseService: [${SERVICE_VERSION}] Waiting for existing initialization...`);
    return initializationPromise;
  }

  console.log(`🔧 SupabaseService: [${SERVICE_VERSION}] Creating new Supabase client instance`);
  
  initializationPromise = new Promise((resolve, reject) => {
    try {
      // Add a small delay to prevent race conditions
      setTimeout(() => {
                 try {
           supabaseInstance = createClientWithDebug(
             environment.supabaseUrl,
             environment.supabaseAnonKey,
             {
               auth: {
                 autoRefreshToken: true,
                 persistSession: true,
                 detectSessionInUrl: true
               }
             }
           );
          
          console.log(`🔧 SupabaseService: [${SERVICE_VERSION}] Supabase client created successfully`);
          resolve(supabaseInstance!);
        } catch (error) {
          console.error(`❌ SupabaseService: [${SERVICE_VERSION}] Failed to create Supabase client:`, error);
          reject(error);
        }
      }, 50);
    } catch (error) {
      console.error(`❌ SupabaseService: [${SERVICE_VERSION}] Failed to create Supabase client:`, error);
      reject(error);
    }
  });

  return initializationPromise;
}

@Injectable({
  providedIn: 'root'
})
export class SupabaseService {
  private _supabase: SupabaseClient | null = null;
  private currentUser = new BehaviorSubject<User | null>(null);
  private examSession = new BehaviorSubject<ExamSession | null>(null);
  private isInitialized = false;
  private serviceId = Math.random().toString(36).substring(7);
  
  // 🔧 NEW: Explicit ready promise that other services can await
  private readyPromise: Promise<void>;

  constructor() {
    console.log(`🔧 SupabaseService: [${SERVICE_VERSION}] Constructor called (Service ID: ${this.serviceId})`);
    console.log(`🔧 SupabaseService: [${SERVICE_VERSION}] Environment config:`, {
      supabaseUrl: environment.supabaseUrl,
      supabaseAnonKey: environment.supabaseAnonKey ? `${environment.supabaseAnonKey.substring(0, 10)}...` : 'NOT_SET',
      production: environment.production
    });

    // 🔧 NEW: Start initialization immediately and expose ready promise
    this.readyPromise = this.initializeClient();
  }

  // 🔧 NEW: Public method for other services to wait for Supabase to be ready
  async ready(): Promise<void> {
    console.log(`🔧 SupabaseService: [${SERVICE_VERSION}] ready() called (Service ID: ${this.serviceId})`);
    await this.readyPromise;
    console.log(`✅ SupabaseService: [${SERVICE_VERSION}] ready() completed (Service ID: ${this.serviceId})`);
  }

  // 🔧 NEW: Public method to get the ready client (synchronous after ready())
  getClient(): SupabaseClient {
    if (!this._supabase) {
      throw new Error('Supabase client not ready. Call ready() first.');
    }
    return this._supabase;
  }

  // 🔧 DEPRECATED: Keep for backward compatibility but mark as deprecated
  get supabase(): SupabaseClient {
    console.warn(`⚠️ SupabaseService: [${SERVICE_VERSION}] Using deprecated 'supabase' getter. Use getClient() after ready() instead.`);
    return this.getClient();
  }

  private async initializeClient(): Promise<void> {
    try {
      this._supabase = await createSupabaseClient();
      console.log(`🔧 SupabaseService: [${SERVICE_VERSION}] Supabase client assigned successfully`);
      
      // Initialize auth after client is ready
      setTimeout(() => {
        this.initializeAuth();
      }, 100);
    } catch (error) {
      console.error(`❌ SupabaseService: [${SERVICE_VERSION}] Failed to initialize client:`, error);
      throw error; // Re-throw so ready() promise rejects
    }
  }

  private async initializeAuth(): Promise<void> {
    if (this.isInitialized || !this._supabase) {
      console.log(`🔧 SupabaseService: [${SERVICE_VERSION}] Auth already initialized or client not ready, skipping`);
      return;
    }

    console.log(`🔧 SupabaseService: [${SERVICE_VERSION}] Starting auth initialization...`);
    
    try {
      // First, get the current session synchronously
      console.log(`🔧 SupabaseService: [${SERVICE_VERSION}] Getting current session...`);
      const { data: { session }, error } = await this._supabase.auth.getSession();
      
      if (error) {
        console.error(`❌ SupabaseService: [${SERVICE_VERSION}] Error getting session:`, error);
        this.currentUser.next(null);
        this.isInitialized = true;
        return;
      }
      
      if (session?.user) {
        console.log(`✅ SupabaseService: [${SERVICE_VERSION}] Found existing session for user:`, session.user.email);
        console.log(`🔧 SupabaseService: [${SERVICE_VERSION}] Session details:`, {
          userId: session.user.id,
          email: session.user.email,
          createdAt: session.user.created_at,
          lastSignIn: session.user.last_sign_in_at
        });
        this.currentUser.next(session.user);
      } else {
        console.log(`ℹ️ SupabaseService: [${SERVICE_VERSION}] No existing session found`);
        this.currentUser.next(null);
      }
      
      // Set up auth state change listener AFTER getting initial session
      console.log(`🔧 SupabaseService: [${SERVICE_VERSION}] Setting up auth state change listener...`);
      this._supabase.auth.onAuthStateChange((event, session) => {
        console.log(`🔄 SupabaseService: [${SERVICE_VERSION}] Auth state changed:`, {
          event,
          userEmail: session?.user?.email,
          userId: session?.user?.id,
          timestamp: new Date().toISOString(),
          serviceId: this.serviceId
        });
        
        if (event === 'SIGNED_IN' && session?.user) {
          console.log(`✅ SupabaseService: [${SERVICE_VERSION}] User signed in:`, session.user.email);
          this.currentUser.next(session.user);
        } else if (event === 'SIGNED_OUT') {
          console.log(`🚪 SupabaseService: [${SERVICE_VERSION}] User signed out`);
          this.currentUser.next(null);
          this.examSession.next(null);
        } else if (event === 'TOKEN_REFRESHED' && session?.user) {
          console.log(`🔄 SupabaseService: [${SERVICE_VERSION}] Token refreshed for user:`, session.user.email);
          this.currentUser.next(session.user);
        } else if (event === 'INITIAL_SESSION') {
          // This is expected when no session exists
          console.log(`ℹ️ SupabaseService: [${SERVICE_VERSION}] Initial session event - no user session found`);
        }
      });
      
      this.isInitialized = true;
      console.log(`✅ SupabaseService: [${SERVICE_VERSION}] Auth initialization completed successfully`);
      
    } catch (error) {
      console.error(`❌ SupabaseService: [${SERVICE_VERSION}] Error initializing auth:`, error);
      this.currentUser.next(null);
      this.isInitialized = true;
    }
  }

  async signUp(email: string, password: string): Promise<AuthResponse> {
    console.log(`🔧 SupabaseService: [${SERVICE_VERSION}] Starting sign up for:`, email);
    try {
      await this.ready();
      const client = this.getClient();
      const response = await client.auth.signUp({
        email,
        password
      });
      
      if (response.error) {
        console.error(`❌ SupabaseService: [${SERVICE_VERSION}] Sign up error:`, response.error);
      } else {
        console.log(`✅ SupabaseService: [${SERVICE_VERSION}] Sign up successful for:`, email);
        console.log(`🔧 SupabaseService: [${SERVICE_VERSION}] Sign up response:`, {
          user: response.data.user?.email,
          session: !!response.data.session
        });
      }
      
      return response;
    } catch (error) {
      console.error(`❌ SupabaseService: [${SERVICE_VERSION}] Sign up failed:`, error);
      throw error;
    }
  }

  async signIn(email: string, password: string): Promise<AuthResponse> {
    console.log(`🔧 SupabaseService: [${SERVICE_VERSION}] Starting sign in for:`, email);
    try {
      await this.ready();
      const client = this.getClient();
      const response = await client.auth.signInWithPassword({
        email,
        password
      });
      
      if (response.error) {
        console.error(`❌ SupabaseService: [${SERVICE_VERSION}] Sign in error:`, response.error);
      } else {
        console.log(`✅ SupabaseService: [${SERVICE_VERSION}] Sign in successful for:`, email);
        console.log(`🔧 SupabaseService: [${SERVICE_VERSION}] Sign in response:`, {
          user: response.data.user?.email,
          session: !!response.data.session
        });
      }
      
      return response;
    } catch (error) {
      console.error(`❌ SupabaseService: [${SERVICE_VERSION}] Sign in failed:`, error);
      throw error;
    }
  }

  async signOut(): Promise<void> {
    console.log(`🔧 SupabaseService: [${SERVICE_VERSION}] Starting sign out...`);
    try {
      await this.ready();
      const client = this.getClient();
      const { error } = await client.auth.signOut();
      if (error) {
        console.error(`❌ SupabaseService: [${SERVICE_VERSION}] SignOut error:`, error);
        throw error;
      }
      console.log(`✅ SupabaseService: [${SERVICE_VERSION}] SignOut successful`);
      this.examSession.next(null);
      this.currentUser.next(null);
    } catch (error) {
      console.error(`❌ SupabaseService: [${SERVICE_VERSION}] SignOut failed:`, error);
      throw error;
    }
  }

  async signInWithGoogle(): Promise<void> {
    console.log(`🔧 SupabaseService: [${SERVICE_VERSION}] Starting Google OAuth sign in...`);
    try {
      await this.ready();
      const client = this.getClient();
      const { error } = await client.auth.signInWithOAuth({ 
        provider: 'google'
      });
      
      if (error) {
        console.error(`❌ SupabaseService: [${SERVICE_VERSION}] Google OAuth error:`, error);
        throw new Error(error.message);
      }
      
      console.log(`✅ SupabaseService: [${SERVICE_VERSION}] Google OAuth initiated successfully`);
    } catch (error) {
      console.error(`❌ SupabaseService: [${SERVICE_VERSION}] Google OAuth failed:`, error);
      throw error;
    }
  }

  getCurrentUser(): Observable<User | null> {
    console.log(`🔧 SupabaseService: [${SERVICE_VERSION}] getCurrentUser() called`);
    return this.currentUser.asObservable();
  }

  getCurrentUserValue(): User | null {
    const user = this.currentUser.value;
    console.log(`🔧 SupabaseService: [${SERVICE_VERSION}] getCurrentUserValue() called, user:`, user?.email || 'null');
    return user;
  }

  async isAuthenticated(): Promise<boolean> {
    console.log(`🔧 SupabaseService: [${SERVICE_VERSION}] Checking authentication status...`);
    try {
      await this.ready();
      const client = this.getClient();
      const { data: { session }, error } = await client.auth.getSession();
      if (error) {
        console.error(`❌ SupabaseService: [${SERVICE_VERSION}] Error checking authentication:`, error);
        return false;
      }
      const isAuth = !!session?.user;
      console.log(`🔧 SupabaseService: [${SERVICE_VERSION}] Authentication status:`, isAuth, 'User:', session?.user?.email || 'null');
      return isAuth;
    } catch (error) {
      console.error(`❌ SupabaseService: [${SERVICE_VERSION}] Error checking authentication:`, error);
      return false;
    }
  }

  // Session management
  async startExamSession(examId: string, totalQuestions: number): Promise<ExamSession> {
    console.log(`🔧 SupabaseService: [${SERVICE_VERSION}] Starting exam session:`, { examId, totalQuestions });
    const user = this.getCurrentUserValue();
    if (!user) {
      console.error(`❌ SupabaseService: [${SERVICE_VERSION}] Cannot start exam session - user not authenticated`);
      throw new Error('User must be authenticated to start an exam session');
    }
    
    const session: Partial<ExamSession> = {
      user_id: user.id,
      exam_id: examId,
      status: 'started',
      current_question: 1,
      total_questions: totalQuestions,
      start_time: new Date().toISOString(),
      last_activity: new Date().toISOString(),
      progress_percentage: 0
    };
    
    console.log(`🔧 SupabaseService: [${SERVICE_VERSION}] Creating exam session in database:`, session);
    
    await this.ready();
    const client = this.getClient();
    const { data, error } = await client
      .from('exam_sessions')
      .insert([session])
      .select()
      .single();
      
    if (error) {
      console.error(`❌ SupabaseService: [${SERVICE_VERSION}] Failed to start exam session:`, error);
      throw new Error(`Failed to start exam session: ${error.message}`);
    }
    
    console.log(`✅ SupabaseService: [${SERVICE_VERSION}] Exam session started successfully:`, data);
    this.examSession.next(data);
    return data;
  }

  async updateExamProgress(questionNumber: number, answer: string, timeSpent: number): Promise<void> {
    console.log(`🔧 SupabaseService: [${SERVICE_VERSION}] Updating exam progress:`, { questionNumber, answer: answer.substring(0, 20) + '...', timeSpent });
    const session = this.examSession.value;
    if (!session) {
      console.error(`❌ SupabaseService: [${SERVICE_VERSION}] Cannot update progress - no active exam session`);
      throw new Error('No active exam session');
    }
    
    // Update progress in database
    const progressData = {
      session_id: session.id,
      question_number: questionNumber,
      answer,
      time_spent: timeSpent
    };
    
    console.log(`🔧 SupabaseService: [${SERVICE_VERSION}] Updating progress in database:`, progressData);
    
    await this.ready();
    const client = this.getClient();
    const { error } = await client
      .from('exam_progress')
      .upsert([progressData]);
      
    if (error) {
      console.error(`❌ SupabaseService: [${SERVICE_VERSION}] Failed to update progress:`, error);
      throw new Error(`Failed to update progress: ${error.message}`);
    }
    
    console.log(`✅ SupabaseService: [${SERVICE_VERSION}] Progress updated successfully`);
    
    // Update session progress
    const progressPercentage = (questionNumber / session.total_questions) * 100;
    await this.updateSessionProgress(questionNumber, progressPercentage);
  }

  private async updateSessionProgress(currentQuestion: number, progressPercentage: number): Promise<void> {
    console.log(`🔧 SupabaseService: [${SERVICE_VERSION}] Updating session progress:`, { currentQuestion, progressPercentage });
    const session = this.examSession.value;
    if (!session) {
      console.log(`ℹ️ SupabaseService: [${SERVICE_VERSION}] No session to update`);
      return;
    }
    
    const updatedSession = {
      ...session,
      current_question: currentQuestion,
      progress_percentage: progressPercentage,
      last_activity: new Date().toISOString()
    };
    
    console.log(`🔧 SupabaseService: [${SERVICE_VERSION}] Updating session in database:`, updatedSession);
    
    await this.ready();
    const client = this.getClient();
    const { error } = await client
      .from('exam_sessions')
      .update(updatedSession)
      .eq('id', session.id);
      
    if (error) {
      console.error(`❌ SupabaseService: [${SERVICE_VERSION}] Failed to update session:`, error);
      return;
    }
    
    console.log(`✅ SupabaseService: [${SERVICE_VERSION}] Session updated successfully`);
    this.examSession.next(updatedSession);
  }

  async completeExamSession(): Promise<void> {
    console.log(`🔧 SupabaseService: [${SERVICE_VERSION}] Completing exam session...`);
    const session = this.examSession.value;
    if (!session) {
      console.log(`ℹ️ SupabaseService: [${SERVICE_VERSION}] No session to complete`);
      return;
    }
    
    await this.ready();
    const client = this.getClient();
    const { error } = await client
      .from('exam_sessions')
      .update({
        status: 'completed',
        last_activity: new Date().toISOString()
      })
      .eq('id', session.id);
      
    if (error) {
      console.error(`❌ SupabaseService: [${SERVICE_VERSION}] Failed to complete exam session:`, error);
      throw new Error(`Failed to complete exam session: ${error.message}`);
    }
    
    console.log(`✅ SupabaseService: [${SERVICE_VERSION}] Exam session completed successfully`);
    this.examSession.next(null);
  }

  getExamSession(): Observable<ExamSession | null> {
    console.log(`🔧 SupabaseService: [${SERVICE_VERSION}] getExamSession() called`);
    return this.examSession.asObservable();
  }

  getExamSessionValue(): ExamSession | null {
    const session = this.examSession.value;
    console.log(`🔧 SupabaseService: [${SERVICE_VERSION}] getExamSessionValue() called, session:`, session?.id || 'null');
    return session;
  }

  // Session recovery: fetch the latest active session for the user
  async recoverActiveSession(examId: string): Promise<ExamSession | null> {
    console.log(`🔧 SupabaseService: [${SERVICE_VERSION}] Recovering active session for exam:`, examId);
    const user = this.getCurrentUserValue();
    if (!user) {
      console.error(`❌ SupabaseService: [${SERVICE_VERSION}] Cannot recover session - user not authenticated`);
      throw new Error('User must be authenticated');
    }
    
    console.log(`🔧 SupabaseService: [${SERVICE_VERSION}] Querying for active sessions...`);
    
    await this.ready();
    const client = this.getClient();
    const { data, error } = await client
      .from('exam_sessions')
      .select('*')
      .eq('user_id', user.id)
      .eq('exam_id', examId)
      .in('status', ['started', 'in_progress', 'paused'])
      .order('start_time', { ascending: false })
      .limit(1)
      .single();
      
    if (error) {
      console.log(`ℹ️ SupabaseService: [${SERVICE_VERSION}] No active session found or error:`, error);
      return null;
    }
    
    console.log(`✅ SupabaseService: [${SERVICE_VERSION}] Active session recovered:`, data);
    this.examSession.next(data);
    return data;
  }
}